/**
 * @fileoverview Provides the `AuthRedirect` component, which redirects authenticated users to the account page using the `useAuth` hook and `Navigate`. It renders its `children` only if the user is not authenticated, as determined by the `shouldRedirect` memoized value. The `AuthRedirectProps` interface defines the component's props.
 */

import React, { useRef, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'

interface AuthRedirectProps {
  children: React.ReactNode
}

export function AuthRedirect({ children }: AuthRedirectProps) {
  const { user, loading } = useAuth()
  const hasRedirected = useRef(false)

  // Cache the initial authentication decision to prevent multiple redirects
  const shouldRedirect = useMemo(() => {
    // If we're still loading, don't make a decision yet
    if (loading) return false

    // If we've already redirected, don't redirect again
    if (hasRedirected.current) return false

    // If user is authenticated, mark that we should redirect
    if (user) {
      hasRedirected.current = true
      return true
    }

    return false
  }, [loading])

  // Show loading state while authentication state is being determined
  if (loading) {
    return <div />
  }

  // If we determined we should redirect, do it
  if (shouldRedirect) {
    return <Navigate to="/account" replace />
  }

  // If not logged in, render the auth form
  return <>{children}</>
}
